import { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import ProductCard from '@/components/ProductCard'
import Skeleton from '@/components/Skeleton'
import { useProductsByCategory } from '@/hooks/useProductsByCategory'
import { useOrg } from '@/context/OrgContext'

export default function CategoryList() {
  const { slug } = useParams()
  const { isRental } = useOrg()
  const { category, products, loading, loadingMore, hasMore, loadMore, notFound } =
    useProductsByCategory(slug, isRental ? 'jual' : undefined)

  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) loadMore() },
      { rootMargin: '0px 0px 400px 0px' } // trigger 400px sebelum sentinel kelihatan
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [loadMore])

  if (notFound) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <p className="text-gray-400 text-sm">Kategori tidak ditemukan.</p>
      </div>
    )
  }

  const title = slug === 'semua' ? 'Semua Produk' : category?.nama

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <h1 className="text-lg font-semibold text-gray-800 mb-4">
        {loading && !title ? <Skeleton className="w-32 h-5" /> : title}
      </h1>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-52" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <p className="text-sm text-gray-400">Belum ada produk di kategori ini.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {products.map(p => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>

          {/* Skeleton muncul di bawah grid, bukan di dalam sentinel */}
          {loadingMore && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-52" />
              ))}
            </div>
          )}

          {/* Sentinel kecil, tidak punya ukuran — hanya trigger observer */}
          <div ref={sentinelRef} className="h-1" />

          {!hasMore && products.length > 0 && (
            <p className="text-center text-xs text-gray-400 mt-4">Semua produk sudah ditampilkan.</p>
          )}
        </>
      )}
    </div>
  )
}
